/**
 * NRN (Newforma Resource Name) parsing and construction utilities.
 *
 * NRN schemas used in this codebase:
 *
 *   Project NRN:
 *     nrn : npc : project : {customerId}(3) : : (4) {server}(5) : {storageId}(6) : {projectId}(7)
 *     index:  0       1         2                  3              4      5               6              7
 *
 *   Folder NRN (fully-qualified, accepted by GET /files):
 *     nrn : file : folder : npc : {customerId}(4) : : (5) {server}(6) : {storageId}(7) : {projectId}(8) : {folderSegments…}
 *     index:  0       1        2      3                  4              5      6               7                  8               9+
 *
 * All parse functions throw if required segments are missing rather than
 * silently returning empty strings, so callers surface errors early.
 */

// ─── Project NRN ────────────────────────────────────────────────────────────

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Returns the stable server-side project ID (UUID) from a project NRN.
 *
 * Rather than relying on a fixed index (which varies across environments and NRN formats),
 * this function finds the last UUID-formatted segment in the NRN  nrn:npc:project:{customerId}::{server}:{storageId}:{projectId}
 * The project UUID is always the last segment matching the 8-4-4-4-12 hex pattern
 *
 * @throws if no UUID segment is found in the NRN.
 */
export function parseProjectNrn(projectNrn: string): { projectId: string } {
    const parts = projectNrn.split(":");
    // Find the last segment that looks like a UUID (8-4-4-4-12 hex).
    // The project UUID is always the last UUID in the NRN regardless of
    // how many segments precede it.
    let projectId: string | undefined;
    for (let i = parts.length - 1; i >= 0; i--) {
        if (UUID_PATTERN.test(parts[i])) {
            projectId = parts[i];
            break;
        }
    }
    if (!projectId) {
        throw new Error(`parseProjectNrn: no UUID projectId segment found in NRN "${projectNrn}"`);
    }
    return { projectId };
}

// ─── Folder NRN ─────────────────────────────────────────────────────────────

/**
 * Returns the stable server-side folder/project ID from a fully-qualified
 * folder NRN (index 8).
 *
 * Folder NRN format:
 *   nrn:file:folder:npc:{customerId}::{server}:{storageId}:{projectId}:{folderSegments…}
 *                                                            ^index 8^
 *
 * @throws if the NRN is missing the required segment.
 */
export function parseFolderNrn(folderNrn: string): { projectFolderId: string } {
    const parts = folderNrn.split(":");
    const projectFolderId = parts[8];
    if (!projectFolderId) {
        throw new Error(`parseFolderNrn: missing projectFolderId segment in NRN "${folderNrn}"`);
    }
    return { projectFolderId };
}

/**
 * Returns true if the NRN identifies a folder resource.
 * Checks index 2 of the NRN ("folder" type segment).
 */
export function isFolderNrn(nrn: string): boolean {
    return nrn.split(":")[2] === "folder";
}

/**
 * Constructs the fully-qualified folder NRN that the GET /files endpoint
 * accepts, given:
 *  - the raw folder NRN returned by the listing API for a folder item
 *  - the selected project's NRN
 *  - the currently active fully-qualified folder NRN (undefined when at root)
 *
 * When already inside a folder the new segment is appended to the active NRN.
 * When at the project root the NRN is constructed from the project NRN.
 *
 * Folder segment extraction: the raw NRN returned by the listing endpoint
 * contains leading fixed/empty segments.  We skip the first 4 parts and
 * collect non-empty trailing parts, allowing up to 4 empty parts (double-colon
 * separators in the schema) before treating the rest as the folder token.
 */
export function buildFolderNrn(
    rawFolderNrn: string,
    selectedProjectNrn: string,
    activeFolderNrn: string | undefined
): string {
    const folderSegment = extractFolderSegment(rawFolderNrn.split(":"));

    if (activeFolderNrn && isFolderNrn(activeFolderNrn)) {
        // Already navigated into a folder — append the new segment.
        return `${activeFolderNrn}:${folderSegment}`;
    }

    // At project root — construct from the project NRN.
    const projectParts = selectedProjectNrn.split(":");
    const projectSegments = projectParts.slice(3).join(":");
    return `nrn:file:folder:npc:${projectSegments}:${folderSegment}`;
}

/**
 * Extracts the meaningful folder name token(s) from a split raw folder NRN.
 *
 * The raw NRN has several leading fixed or empty segments before the folder
 * token.  We start at index 4 and skip up to 4 empty parts (the double-colon
 * separators), then collect all remaining non-empty parts.
 */
function extractFolderSegment(parts: string[]): string {
    const segments: string[] = [];
    let emptyCount = 0;
    for (let i = 4; i < parts.length; i++) {
        if (parts[i] === "" && emptyCount < 4) {
            emptyCount++;
        } else if (parts[i] !== "") {
            segments.push(parts[i]);
        }
    }
    return segments.join(":");
}

export function buildEmailFolderNrn(rawFolderNrn: string, selectedProjectNrn: string, emailFolderName: string): string {
    const folderSegment = extractFolderSegment(rawFolderNrn.split(":"));
    if (!folderSegment) {
        throw new Error(`buildEmailFolderNrn: could not extract a folder segment from rawFolderNrn: "${rawFolderNrn}"`);
    }

    const projectParts = selectedProjectNrn.split(":");
    if (projectParts.length < 4) {
        throw new Error(
            `buildEmailFolderNrn: selectedProjectNrn "${selectedProjectNrn}" is malformed — expected at least 4 ":"-delimited segments, got ${projectParts.length}`
        );
    }

    // At project root — construct from the project NRN.
    const projectSegments = projectParts.slice(3).join(":");
    if (!projectSegments) {
        throw new Error(
            `buildEmailFolderNrn: projectSegments resolved to empty for selectedProjectNrn: "${selectedProjectNrn}"`
        );
    }

    return `nrn:file:folder:npc:${projectSegments}:${folderSegment}:${emailFolderName}`;
}
